import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Lock, LockOpen, Copy, ShieldCheck, ShieldAlert, Building2,
  ScrollText, QrCode, Clock, FileText, TrendingUp, MessageSquare, User, Fingerprint, LifeBuoy,
} from "lucide-react";
import { motion } from "framer-motion";

import { api, apiErrorText } from "@/lib/api";
import {
  VLKPageHeader, VLKCard, VLKCardHeader, VLKCardTitle, VLKCardContent, VLKCardDescription,
  VLKButton, VLKStatusBadge, VLKRiskMeter, VLKKPICard, VLKSkeleton, VLKEmptyState,
  VLKAlertDialog, VLKLabel, VLKTextarea, VLKSelect, VLKTabs, VLKUserAvatar,
  toast,
} from "@/components/vlk";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

/* --------- Small internal presentational pieces --------- */

function DataRow({ label, value, mono = false }) {
  return (
    <div className="flex items-baseline gap-4 py-2 border-b border-border/60 last:border-0">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium w-40 shrink-0">{label}</div>
      <div className={cn("text-sm", mono && "font-mono text-xs")}>{value || <span className="text-muted-foreground">—</span>}</div>
    </div>
  );
}

function TimelineEvent({ event }) {
  const iconOf = (action) => {
    if (action.startsWith("IDENTITY_LOCKED") || action === "EMERGENCY_LOCK") return Lock;
    if (action === "IDENTITY_UNLOCKED") return LockOpen;
    if (action.startsWith("QR_")) return QrCode;
    if (action === "USER_DENIED") return ShieldAlert;
    if (action === "IDENTITY_NOTE_ADDED") return MessageSquare;
    if (action.startsWith("INSTITUTION")) return Building2;
    return Fingerprint;
  };
  const Icon = iconOf(event.action);
  const critical = event.risk_level === "critical" || event.risk_level === "high" || event.outcome === "DENIED";
  return (
    <div className="flex gap-3 relative">
      <div className="relative flex flex-col items-center">
        <div className={cn(
          "h-8 w-8 rounded-sm border flex items-center justify-center shrink-0 z-10 bg-card",
          critical ? "border-destructive/40 text-destructive" : "border-border text-muted-foreground"
        )}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="absolute top-8 bottom-0 w-px bg-border" />
      </div>
      <div className="flex-1 pb-6 min-w-0">
        <div className="flex items-baseline gap-3 flex-wrap">
          <div className="font-mono text-xs uppercase tracking-widest text-foreground">{event.action}</div>
          <VLKStatusBadge status={event.outcome === "SUCCESS" || event.outcome === "ISSUED" ? "active" : event.outcome === "BURNED" ? "verified" : "rejected"}>
            {event.outcome}
          </VLKStatusBadge>
          <VLKRiskMeter level={event.risk_level} className="ml-auto" />
        </div>
        <div className="text-xs text-muted-foreground font-mono mt-1">
          {new Date(event.timestamp).toLocaleString()}
          {event.actor_email && <> · <span className="text-foreground">{event.actor_email}</span> · {event.role}</>}
        </div>
        {event.integrity_hash && (
          <div className="text-[10px] font-mono text-muted-foreground/70 mt-1">integrity: {event.integrity_hash}…</div>
        )}
      </div>
    </div>
  );
}

/* --------- Main page --------- */

export default function IdentityDetail() {
  const { vlk_id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user: me } = useAuth();

  const [tab, setTab] = useState("timeline");
  const [action, setAction] = useState(null); // "lock" | "unlock" | "note" | null
  const [reason, setReason] = useState("");
  const [tier, setTier] = useState("owner");
  const [note, setNote] = useState("");
  const [noteCategory, setNoteCategory] = useState("general");
  const [busy, setBusy] = useState(false);

  const { data: identity, isLoading } = useQuery({
    queryKey: ["identity", vlk_id],
    queryFn: () => api.get(`/identities/${vlk_id}`).then(r => r.data),
    enabled: !!vlk_id,
  });

  const { data: timeline } = useQuery({
    queryKey: ["identity-timeline", vlk_id],
    queryFn: () => api.get(`/identities/${vlk_id}/timeline?limit=100`).then(r => r.data),
    enabled: !!vlk_id,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["identity", vlk_id] });
    qc.invalidateQueries({ queryKey: ["identity-timeline", vlk_id] });
  };

  const doLock = async () => {
    setBusy(true);
    try {
      await api.post("/lock", { vlk_id, reason, tier });
      toast.success(`${vlk_id} locked`, { description: `Tier: ${tier}` });
      setAction(null); setReason("");
      refresh();
    } catch (e) { toast.error("Lock failed", { description: apiErrorText(e) }); }
    finally { setBusy(false); }
  };

  const doUnlock = async () => {
    setBusy(true);
    try {
      await api.post("/unlock", { vlk_id, reason });
      toast.success(`${vlk_id} unlocked`);
      setAction(null); setReason("");
      refresh();
    } catch (e) { toast.error("Unlock failed", { description: apiErrorText(e) }); }
    finally { setBusy(false); }
  };

  const doAddNote = async () => {
    setBusy(true);
    try {
      await api.post(`/identities/${vlk_id}/note`, { body: note, category: noteCategory });
      toast.success("Note added");
      setAction(null); setNote(""); setNoteCategory("general");
      refresh();
    } catch (e) { toast.error("Failed", { description: apiErrorText(e) }); }
    finally { setBusy(false); }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <VLKSkeleton className="h-24 w-full" />
        <div className="grid grid-cols-4 gap-4"><VLKSkeleton className="h-24" /><VLKSkeleton className="h-24" /><VLKSkeleton className="h-24" /><VLKSkeleton className="h-24" /></div>
        <VLKSkeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!identity) {
    return (
      <VLKEmptyState icon={User} title="Identity not found" description={`No identity matches ${vlk_id}.`}
        action={<VLKButton variant="outline" onClick={() => navigate("/identities")}>Back to registry</VLKButton>} />
    );
  }

  const isLocked = identity.lock_state === "LOCKED";
  const canLock = ["system_admin", "security_officer", "identity_officer"].includes(me?.role);
  const inRecovery = identity.recovery_status === "IN_PROGRESS";
  const passedVerifs = (identity.verification_history || []).filter(v => v.outcome === "PASSED").length;
  const totalVerifs = (identity.verification_history || []).length;

  return (
    <div>
      {/* Back nav */}
      <Link to="/identities" data-testid="detail-back" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-3.5 w-3.5" /> Identity registry
      </Link>

      {/* Header block */}
      <motion.div
        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-card border border-border rounded p-6 mb-6"
      >
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <VLKUserAvatar name={identity.full_name} size={56} />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display text-2xl tracking-tight truncate">{identity.full_name}</h1>
                <VLKStatusBadge status={identity.lock_state?.toLowerCase()}>{identity.lock_state}</VLKStatusBadge>
                {inRecovery && <VLKStatusBadge status="pending">recovery</VLKStatusBadge>}
              </div>
              <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard?.writeText(identity.vlk_id);
                      toast.success("VLK_ID copied");
                    } catch {
                      toast.error("Copy blocked — select and copy manually");
                    }
                  }}
                  className="font-mono text-xs inline-flex items-center gap-1.5 hover:text-foreground group"
                  data-testid="detail-copy-vlk"
                >
                  {identity.vlk_id}
                  <Copy className="h-3 w-3 opacity-50 group-hover:opacity-100" />
                </button>
                <span>·</span><span>{identity.email}</span>
                <span>·</span><span className="font-mono text-xs">{identity.phone}</span>
              </div>
              <div className="flex items-center gap-4 mt-3 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Risk</span>
                  <VLKRiskMeter level={identity.risk_level} />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Verification level</span>
                  <span className="font-mono text-xs uppercase tracking-widest">{identity.verification_level}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 shrink-0">
            {canLock && (isLocked ? (
              <VLKButton variant="primary" data-testid="detail-unlock-btn" onClick={() => { setAction("unlock"); setReason(""); }}>
                <LockOpen className="h-4 w-4" />Unlock identity
              </VLKButton>
            ) : (
              <VLKButton variant="destructive" data-testid="detail-lock-btn" onClick={() => { setAction("lock"); setReason(""); setTier("owner"); }}>
                <Lock className="h-4 w-4" />Lock identity
              </VLKButton>
            ))}
            <VLKButton variant="outline" data-testid="detail-note-btn" onClick={() => { setAction("note"); setNote(""); }}>
              <MessageSquare className="h-4 w-4" />Add note
            </VLKButton>
            <VLKButton
              variant="outline"
              data-testid="identity-initiate-recovery"
              onClick={() => navigate(`/recovery?prefill=${identity.vlk_id}`)}
            >
              <LifeBuoy className="h-4 w-4" />
              {inRecovery ? "View recovery" : "Initiate recovery"}
            </VLKButton>
          </div>
        </div>
      </motion.div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <VLKKPICard label="Audit events" value={identity.counters?.audit_events ?? 0} icon={ScrollText} testId="ident-kpi-audit" />
        <VLKKPICard label="Verifications" value={`${passedVerifs}/${totalVerifs}`} icon={ShieldCheck} testId="ident-kpi-verifs" tone={passedVerifs === totalVerifs && totalVerifs > 0 ? "success" : "default"} />
        <VLKKPICard label="Linked institutions" value={identity.counters?.linked_institutions ?? 0} icon={Building2} testId="ident-kpi-inst" />
        <VLKKPICard label="QR sessions" value={identity.counters?.qr_sessions ?? 0} icon={QrCode} testId="ident-kpi-qr" />
      </div>

      {/* Tabs + content */}
      <VLKTabs
        value={tab}
        onChange={setTab}
        items={[
          { value: "timeline", label: `Timeline (${timeline?.items?.length ?? 0})` },
          { value: "profile", label: "Profile" },
          { value: "verifications", label: `Verifications (${totalVerifs})` },
          { value: "institutions", label: `Institutions (${identity.linked_institutions?.length ?? 0})` },
          { value: "notes", label: `Notes (${identity.notes?.length ?? 0})` },
        ]}
      />

      <div className="mt-6" data-testid={`detail-tab-panel-${tab}`}>
        {tab === "timeline" && (
          <VLKCard>
            <VLKCardHeader>
              <VLKCardTitle>Tamper-evident activity timeline</VLKCardTitle>
              <VLKCardDescription>Combined audit ledger + QR sessions for this identity. Every row is integrity-hashed.</VLKCardDescription>
            </VLKCardHeader>
            <VLKCardContent>
              {(timeline?.items || []).length === 0 ? (
                <VLKEmptyState icon={Clock} title="No activity yet" description="Events created after this identity was registered will appear here." />
              ) : (
                <div>
                  {(timeline?.items || []).map((event) => (
                    <TimelineEvent key={event.id} event={event} />
                  ))}
                </div>
              )}
            </VLKCardContent>
          </VLKCard>
        )}

        {tab === "profile" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <VLKCard>
              <VLKCardHeader><VLKCardTitle>Identity profile</VLKCardTitle></VLKCardHeader>
              <VLKCardContent>
                <DataRow label="VLK_ID" value={identity.vlk_id} mono />
                <DataRow label="Full name" value={identity.full_name} />
                <DataRow label="Email" value={identity.email} mono />
                <DataRow label="Phone" value={identity.phone} mono />
                <DataRow label="Country" value={identity.country} mono />
                <DataRow label="State" value={identity.state} />
                <DataRow label="Verification level" value={identity.verification_level} mono />
                <DataRow label="Registered" value={identity.created_at && new Date(identity.created_at).toLocaleString()} mono />
                <DataRow label="Last activity" value={identity.last_activity_at && new Date(identity.last_activity_at).toLocaleString()} mono />
              </VLKCardContent>
            </VLKCard>

            <VLKCard>
              <VLKCardHeader>
                <VLKCardTitle>Security status</VLKCardTitle>
                <VLKCardDescription>Current lock state, recovery workflow and risk posture.</VLKCardDescription>
              </VLKCardHeader>
              <VLKCardContent>
                <DataRow label="Lock state" value={<VLKStatusBadge status={identity.lock_state?.toLowerCase()}>{identity.lock_state}</VLKStatusBadge>} />
                {isLocked && <>
                  <DataRow label="Lock tier" value={identity.lock_tier} mono />
                  <DataRow label="Locked at" value={identity.locked_at && new Date(identity.locked_at).toLocaleString()} mono />
                  <DataRow label="Locked reason" value={identity.locked_reason} />
                </>}
                <DataRow label="Risk level" value={<VLKRiskMeter level={identity.risk_level} />} />
                <DataRow label="Recovery status" value={<VLKStatusBadge status={inRecovery ? "pending" : "active"}>{identity.recovery_status || "NONE"}</VLKStatusBadge>} />
                <DataRow label="Verifications count" value={identity.verifications_count} mono />
              </VLKCardContent>
            </VLKCard>
          </div>
        )}

        {tab === "verifications" && (
          <VLKCard>
            <VLKCardHeader>
              <VLKCardTitle>Verification history</VLKCardTitle>
              <VLKCardDescription>All identity verifications performed by authorized institutions.</VLKCardDescription>
            </VLKCardHeader>
            <VLKCardContent className="p-0">
              {(identity.verification_history || []).length === 0 ? (
                <div className="p-6"><VLKEmptyState icon={ShieldCheck} title="No verifications recorded" /></div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 border-b border-border">
                    <tr>
                      {["Method","Level","Institution","Confidence","Outcome","When"].map(h => (
                        <th key={h} className="px-4 py-2.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {identity.verification_history.map(v => (
                      <tr key={v.verification_id} className="border-b border-border last:border-0 hover:bg-secondary/40">
                        <td className="px-4 py-2.5 font-mono text-xs">{v.method}</td>
                        <td className="px-4 py-2.5"><VLKStatusBadge status="verified">{v.level}</VLKStatusBadge></td>
                        <td className="px-4 py-2.5">{v.verified_by_institution_name || <span className="text-muted-foreground">—</span>}</td>
                        <td className="px-4 py-2.5 font-mono text-xs tabular">{Math.round(v.confidence * 100)}%</td>
                        <td className="px-4 py-2.5">
                          <span className={cn("text-[10px] uppercase tracking-widest font-mono",
                            v.outcome === "PASSED" ? "text-success" : "text-destructive"
                          )}>{v.outcome}</span>
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{new Date(v.verified_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </VLKCardContent>
          </VLKCard>
        )}

        {tab === "institutions" && (
          <VLKCard>
            <VLKCardHeader>
              <VLKCardTitle>Institution relationships</VLKCardTitle>
              <VLKCardDescription>Organizations linked to this identity.</VLKCardDescription>
            </VLKCardHeader>
            <VLKCardContent className="p-0">
              {(identity.linked_institutions || []).length === 0 ? (
                <div className="p-6"><VLKEmptyState icon={Building2} title="No institutions linked yet" /></div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 border-b border-border">
                    <tr>
                      {["VLK_INST_ID","Institution","Type","Relationship","Linked"].map(h => (
                        <th key={h} className="px-4 py-2.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {identity.linked_institutions.map(inst => (
                      <tr key={inst.vlk_inst_id + inst.linked_at} className="border-b border-border last:border-0 hover:bg-secondary/40">
                        <td className="px-4 py-2.5 font-mono text-xs">{inst.vlk_inst_id}</td>
                        <td className="px-4 py-2.5 font-medium">{inst.name}</td>
                        <td className="px-4 py-2.5"><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{inst.type}</span></td>
                        <td className="px-4 py-2.5"><VLKStatusBadge status="verified">{inst.relationship}</VLKStatusBadge></td>
                        <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{new Date(inst.linked_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </VLKCardContent>
          </VLKCard>
        )}

        {tab === "notes" && (
          <VLKCard>
            <VLKCardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <VLKCardTitle>Administrator notes</VLKCardTitle>
                  <VLKCardDescription>Append-only. Every note writes an audit event.</VLKCardDescription>
                </div>
                <VLKButton variant="outline" size="sm" onClick={() => { setAction("note"); setNote(""); }}>
                  <MessageSquare className="h-3.5 w-3.5" />Add note
                </VLKButton>
              </div>
            </VLKCardHeader>
            <VLKCardContent>
              {(identity.notes || []).length === 0 ? (
                <VLKEmptyState icon={MessageSquare} title="No notes yet" description="Add the first operational note for this identity." />
              ) : (
                <div className="space-y-3">
                  {identity.notes.slice().reverse().map(n => (
                    <div key={n.note_id} className="border border-border rounded p-4 bg-background">
                      <div className="flex items-center gap-2 mb-2">
                        <VLKUserAvatar name={n.author_email} size={24} />
                        <span className="text-sm font-medium">{n.author_email}</span>
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">{n.author_role}</span>
                        <VLKStatusBadge status={n.category === "security" ? "locked" : n.category === "compliance" ? "verified" : "active"} className="ml-2">{n.category}</VLKStatusBadge>
                        <span className="ml-auto text-xs font-mono text-muted-foreground">{new Date(n.created_at).toLocaleString()}</span>
                      </div>
                      <div className="text-sm whitespace-pre-wrap">{n.body}</div>
                    </div>
                  ))}
                </div>
              )}
            </VLKCardContent>
          </VLKCard>
        )}
      </div>

      {/* Lock dialog */}
      <VLKAlertDialog
        open={action === "lock"}
        onOpenChange={() => setAction(null)}
        title={`Lock ${identity.vlk_id}`}
        tone="destructive"
        actions={
          <>
            <VLKButton variant="ghost" onClick={() => setAction(null)}>Cancel</VLKButton>
            <VLKButton variant="destructive" onClick={doLock} loading={busy} disabled={reason.length < 3} data-testid="detail-lock-submit">Confirm lock</VLKButton>
          </>
        }
      >
        <div className="space-y-3">
          <div className="text-sm">You are about to lock <span className="font-mono">{identity.full_name}</span>. This action is written to the immutable audit ledger.</div>
          <div className="space-y-1.5">
            <VLKLabel>Tier</VLKLabel>
            <VLKSelect value={tier} onChange={(e) => setTier(e.target.value)} data-testid="detail-lock-tier">
              <option value="owner">Owner (self)</option>
              <option value="institution">Institution</option>
              <option value="temporary">Temporary (24h)</option>
              <option value="emergency">Emergency (requires security_officer)</option>
              <option value="permanent">Permanent</option>
            </VLKSelect>
          </div>
          <div className="space-y-1.5">
            <VLKLabel>Reason (required)</VLKLabel>
            <VLKTextarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Describe the reason for the lock (auditable)…" data-testid="detail-lock-reason" />
          </div>
        </div>
      </VLKAlertDialog>

      {/* Unlock dialog */}
      <VLKAlertDialog
        open={action === "unlock"}
        onOpenChange={() => setAction(null)}
        title={`Unlock ${identity.vlk_id}`}
        actions={
          <>
            <VLKButton variant="ghost" onClick={() => setAction(null)}>Cancel</VLKButton>
            <VLKButton variant="primary" onClick={doUnlock} loading={busy} disabled={reason.length < 3} data-testid="detail-unlock-submit">Confirm unlock</VLKButton>
          </>
        }
      >
        <div className="space-y-3">
          <div className="text-sm">Unlock <span className="font-mono">{identity.full_name}</span>? This is auditable and reversible.</div>
          <div className="space-y-1.5">
            <VLKLabel>Reason (required)</VLKLabel>
            <VLKTextarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="detail-unlock-reason" />
          </div>
        </div>
      </VLKAlertDialog>

      {/* Note dialog */}
      <VLKAlertDialog
        open={action === "note"}
        onOpenChange={() => setAction(null)}
        title={`Add note to ${identity.vlk_id}`}
        actions={
          <>
            <VLKButton variant="ghost" onClick={() => setAction(null)}>Cancel</VLKButton>
            <VLKButton variant="primary" onClick={doAddNote} loading={busy} disabled={note.length < 1} data-testid="detail-note-submit">Add note</VLKButton>
          </>
        }
      >
        <div className="space-y-3">
          <div className="space-y-1.5">
            <VLKLabel>Category</VLKLabel>
            <VLKSelect value={noteCategory} onChange={(e) => setNoteCategory(e.target.value)}>
              <option value="general">General</option>
              <option value="compliance">Compliance</option>
              <option value="security">Security</option>
              <option value="recovery">Recovery</option>
            </VLKSelect>
          </div>
          <div className="space-y-1.5">
            <VLKLabel>Note</VLKLabel>
            <VLKTextarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Operational note (auditable)…" rows={4} data-testid="detail-note-input" />
          </div>
        </div>
      </VLKAlertDialog>
    </div>
  );
}
